import React, { useState } from "react";
import {
  SingleWordMatchQuestion,
  SingleWordMatchOption,
  ActivityMeta,
} from "src/types";
import showToast from "@utils/toast";
import { useActivityCoreData } from "src/hooks/useActivityCoreData";
import { newActivity, updateActivity } from "src/service/activities.services";
import ActivityCore from "./activities.core";
import { getAssetUrl, getNextSerialId } from "@utils/id.generator";
import { CheckCircle2, Circle, Edit, Trash2, Save, X } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface ISingleWordMatchFormProps {
  lessonMeta?: ActivityMeta;
  initialActivity?: SingleWordMatchQuestion;
  activityId: string;
}

const SingleWordMatchActivityForm = ({
  lessonMeta,
  initialActivity,
  activityId,
}: ISingleWordMatchFormProps) => {
  const isEditing = !!initialActivity;

  const navigate = useNavigate();

  const [isLoading, setLoading] = useState<boolean>(false);
  const [editIdx, setEditIdx] = useState<number | null>(null);
  const [questionText, setQuestionText] = useState(
    initialActivity?.data.question?.text || ""
  );
  const [audioUrl, setAudioUrl] = useState(
    initialActivity?.data.question?.audioUrl || ""
  );

  const [options, setOptions] = useState<SingleWordMatchOption[]>(
    initialActivity?.data.options || []
  );
  const [answer, setAnswer] = useState<string[]>(
    initialActivity?.data.answer || []
  );
  const [newOption, setNewOption] = useState({
    text: "",
    audioUrl: "",
  });

  const [draggedIdx, setDraggedIdx] = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);

  const reorder = <T,>(list: T[], from: number, to: number): T[] => {
    const copy = [...list];
    const [moved] = copy.splice(from, 1);
    copy.splice(to, 0, moved);
    return copy;
  };

  const handleDrop = () => {
    if (
      draggedIdx === null ||
      dragOverIdx === null ||
      draggedIdx === dragOverIdx
    )
      return;

    const newOptions = reorder(options, draggedIdx, dragOverIdx);
    setOptions(newOptions);
    const newAnswer = newOptions
      .filter((opt) => answer.includes(opt.id))
      .map((opt) => opt.id);
    setAnswer(newAnswer);

    setDraggedIdx(null);
    setDragOverIdx(null);
  };

  const addOption = () => {
    if (!newOption.text.trim() && !newOption.audioUrl.trim()) {
      return showToast(
        "Either text or audio code is required for each option",
        false
      );
    }
    const optionId = getNextSerialId(options);
    const opt: SingleWordMatchOption = {
      id: optionId,
      text: newOption.text.trim(),
      audioUrl: newOption.audioUrl || undefined,
    };
    setOptions((prev) => [...prev, opt]);
    setNewOption({ text: "", audioUrl: "" });
  };

  const toggleAnswer = (id: string) => {
    setAnswer((prev) =>
      prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]
    );
  };

  const coreState = useActivityCoreData({
    instruction: initialActivity?.instruction,
    localizedInstruction: initialActivity?.localizedInstruction,
    difficulty: initialActivity?.difficulty,
    tags: initialActivity?.tags,
    isActive: initialActivity?.isActive || false,
    activityNumber: initialActivity?.activityNumber || 0,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    setLoading(true);
    e.preventDefault();
    const isValidId = isEditing ? initialActivity?.id : activityId;
    if (!isValidId || isValidId === null || isValidId === undefined) {
      return showToast("Activity Id not provided", false);
    }

    if (questionText.trim() === "" && audioUrl.trim() === "") {
      setLoading(false);
      return showToast(
        "Either one of question audio code or text must be provided!",
        false
      );
    }

    let QuestionState: SingleWordMatchQuestion["data"]["question"] = {
      text: questionText,
      audioUrl: audioUrl || undefined,
    };

    if (!audioUrl) {
      QuestionState = {
        text: questionText,
      } as SingleWordMatchQuestion["data"]["question"];
    } else if (!questionText) {
      QuestionState = {
        audioUrl,
      } as SingleWordMatchQuestion["data"]["question"];
    }
    // If both are provided, use both
    else {
      QuestionState = {
        text: questionText,
        audioUrl,
      } as SingleWordMatchQuestion["data"]["question"];
    }

    const payload: SingleWordMatchQuestion = {
      id: isValidId,
      activityKind: "MatchTheCorrectWord",
      instruction: coreState.coreData.instruction,
      localizedInstruction: coreState.coreData.localizedInstruction,
      activityNumber: coreState.coreData.activityNumber,
      lessonId: initialActivity?.lessonId ?? (lessonMeta?.lesson as string),
      difficulty: coreState.coreData.difficulty,
      tags: coreState.coreData.tags,
      isActive: coreState.coreData.isActive,
      data: {
        question: QuestionState,
        options,
        answer: answer || [],
      },
    };

    const request = isEditing
      ? updateActivity(payload, isValidId)
      : newActivity(payload);
    const response = await request;
    if (response?.status !== "success") {
      showToast("There was error saving activity", false);
      setLoading(false);
    } else {
      setLoading(false);
      showToast(
        `${isEditing ? "Activity Updated" : "Activity Created"} successfully`,
        true
      );
      return navigate("/dashboard/levels/lessons/activities");
    }

  };

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-8 max-w-4xl mx-auto bg-white p-6 rounded shadow"
    >
      <h2 className="text-base font-semibold text-gray-800">
        {isEditing ? "Edit" : "Create"} Match the Correct Word
      </h2>

      <ActivityCore data={coreState} />
      <div className="grid sm:grid-cols-2 gap-4">
        <input
          className="input-style"
          placeholder="Question Text"
          value={questionText}
          onChange={(e) => setQuestionText(e.target.value)}
        />
        <input
          className="input-style"
          placeholder="Audio Code"
          value={audioUrl}
          maxLength={6}
          min={6}
          onChange={(e) => setAudioUrl(e.target.value)}
        />
      </div>

      {audioUrl && (
        <audio controls className="mt-2 w-full">
          <source src={getAssetUrl("audio", audioUrl)} type="audio/mpeg" />
        </audio>
      )}

      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-gray-800">Add Option</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <input
            className="input-style"
            placeholder="Text"
            value={newOption.text}
            onChange={(e) =>
              setNewOption({ ...newOption, text: e.target.value })
            }
          />
          <input
            className="input-style"
            placeholder="Audio Code"
            maxLength={6}
            min={6}
            value={newOption.audioUrl}
            onChange={(e) =>
              setNewOption({ ...newOption, audioUrl: e.target.value })
            }
          />
        </div>
        <button
          type="button"
          onClick={addOption}
          className="text-sm text-brand hover:underline"
        >
          + Add Option
        </button>
      </div>

      {options.length > 0 && (
        <div className="space-y-3 pt-4">
          <h3 className="text-sm font-semibold text-gray-800">
            Answer Order Preview
          </h3>
          <div
            className="flex overflow-x-auto gap-4 pb-2"
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
          >
            {options.map((opt, idx) => {
              const isEditing = editIdx === idx;
              return (
                <div
                  key={opt.id}
                  draggable={!isEditing}
                  onDragStart={() => setDraggedIdx(idx)}
                  onDragEnter={() => setDragOverIdx(idx)}
                  className={`min-w-[200px] max-w-[200px] flex-shrink-0 p-4 rounded-lg border bg-white shadow-md transition hover:shadow-lg
              ${answer.includes(opt.id) ? "border-brand" : "border-gray-200"}
              ${draggedIdx === idx ? "opacity-50" : ""}
            `}
                >
                  {isEditing ? (
                    <>
                      <input
                        type="text"
                        value={opt.text}
                        onChange={(e) =>
                          setOptions((prev) =>
                            prev.map((o, i) =>
                              i === idx ? { ...o, text: e.target.value } : o
                            )
                          )
                        }
                        className="w-full px-2 py-1 border rounded-md focus:ring focus:ring-brand mb-2"
                        placeholder="Option Text"
                      />
                      <input
                        type="text"
                        value={opt.audioUrl || ""}
                        onChange={(e) =>
                          setOptions((prev) =>
                            prev.map((o, i) =>
                              i === idx ? { ...o, audioUrl: e.target.value } : o
                            )
                          )
                        }
                        className="w-full px-2 py-1 border rounded-md focus:ring focus:ring-brand mb-2"
                        placeholder="Audio Code"
                      />
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => setEditIdx(null)}
                          className="text-green-600 hover:text-green-800"
                          title="Save"
                        >
                          <Save size={18} />
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditIdx(null)}
                          className="text-gray-500 hover:text-gray-700"
                          title="Cancel"
                        >
                          <X size={18} />
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex justify-between items-center mb-2">
                        <p className="text-sm font-medium text-gray-800 truncate">
                          {opt.text}
                        </p>
                        <p className="text-xs font-semibold text-gray-600 truncate">
                          {opt.audioUrl || "No Code"}
                        </p>
                      </div>

                      {opt.audioUrl && (
                        <audio controls className="w-full h-7 rounded">
                          <source
                            src={getAssetUrl("audio", opt.audioUrl)}
                            type="audio/mpeg"
                          />
                        </audio>
                      )}

                      <div className="flex justify-between items-center mt-3">
                        <button
                          type="button"
                          onClick={() => toggleAnswer(opt.id)}
                          className={`flex items-center gap-1 text-xs px-2 py-1 rounded transition ${
                            answer.includes(opt.id)
                              ? "bg-emerald-600 text-white hover:bg-emerald-700"
                              : "border border-emerald-400 text-emerald-600 hover:bg-emerald-50"
                          }`}
                          title={
                            answer.includes(opt.id) ? "Selected" : "Select"
                          }
                        >
                          {answer.includes(opt.id) ? "✓ Selected" : "Select"}
                        </button>

                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => setEditIdx(idx)}
                            className="text-blue-500 hover:text-blue-700"
                            title="Edit"
                          >
                            <Edit size={18} />
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setOptions((prev) =>
                                prev.filter((_, i) => i !== idx)
                              );
                              setAnswer((ans) =>
                                ans.filter((id) => id !== opt.id)
                              );
                            }}
                            className="text-red-500 hover:text-red-700"
                            title="Remove"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="pt-4">
        <button type="submit" className="button-primary">
          {isEditing ? "Update Activity" : "Create Activity"}
        </button>
      </div>
    </form>
  );
};

export default SingleWordMatchActivityForm;
